import { ADJUST_EXPORT_JPEG_QUALITY, AdjustAdjustments, AdjustBlur, AdjustDocument } from './adjust.types'
import { Size, compose, frameRectOf, imageToFrame, outputSize, translation } from './adjustGeometry'
import { StickerAssets, drawAdjustSticker } from './adjustStickers'
import { blurBounds, drawAdjustShape, traceBlurMask } from './adjustShapes'
import { isNeutral, mergeAdjustments, processPixels } from './adjustColor'

import { WebGLAdjustProcessor } from './webglAdjustProcessor'

export type RenderSource = HTMLCanvasElement | HTMLImageElement

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))
  return canvas
}

const context2d = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('adjust_canvas_unavailable')
  return ctx
}

const supportsCanvasFilter = () => typeof CanvasRenderingContext2D !== 'undefined' && 'filter' in CanvasRenderingContext2D.prototype

const sourceIds = new WeakMap<object, number>()
let nextSourceId = 1
const sourceId = (source: object) => {
  if (!sourceIds.has(source)) sourceIds.set(source, nextSourceId++)
  return sourceIds.get(source)
}

/**
 * Copies a region of `source` into a new canvas padded by `pad` on every side, the padding filled by stretching the
 * region's edge pixels — so a blur near the photo's border does not fade into transparency (and leak detail).
 */
const paddedRegion = (source: HTMLCanvasElement, x: number, y: number, width: number, height: number, pad: number): HTMLCanvasElement => {
  const canvas = createCanvas(width + 2 * pad, height + 2 * pad)
  const ctx = context2d(canvas)
  ctx.drawImage(source, x, y, width, height, pad, pad, width, height)
  if (pad > 0) {
    ctx.drawImage(source, x, y, 1, height, 0, pad, pad, height)
    ctx.drawImage(source, x + width - 1, y, 1, height, pad + width, pad, pad, height)
    ctx.drawImage(source, x, y, width, 1, pad, 0, width, pad)
    ctx.drawImage(source, x, y + height - 1, width, 1, pad, pad + height, width, pad)
    ctx.drawImage(source, x, y, 1, 1, 0, 0, pad, pad)
    ctx.drawImage(source, x + width - 1, y, 1, 1, pad + width, 0, pad, pad)
    ctx.drawImage(source, x, y + height - 1, 1, 1, 0, pad + height, pad, pad)
    ctx.drawImage(source, x + width - 1, y + height - 1, 1, 1, pad + width, pad + height, pad, pad)
  }
  return canvas
}

/** Gaussian blur through the canvas filter; browsers without it get a two-pass downscale blur */
const blurCanvas = (input: HTMLCanvasElement, radius: number): HTMLCanvasElement => {
  const output = createCanvas(input.width, input.height)
  const ctx = context2d(output)
  if (supportsCanvasFilter()) {
    ctx.filter = `blur(${radius}px)`
    ctx.drawImage(input, 0, 0)
    ctx.filter = 'none'
    return output
  }
  const factor = Math.max(1.5, radius / 2)
  const small = createCanvas(input.width / factor, input.height / factor)
  const smallCtx = context2d(small)
  smallCtx.imageSmoothingQuality = 'high'
  smallCtx.drawImage(input, 0, 0, small.width, small.height)
  smallCtx.drawImage(small, 0, 0, small.width, small.height)
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(small, 0, 0, output.width, output.height)
  return output
}

/**
 * Renders a document over a photo: colour (WebGL, or the CPU path) → blurs → drawing → stickers. One instance for the
 * live preview (it caches the colour pass and every blur between frames) and a throwaway one for each save.
 */
export class AdjustRenderer {
  private readonly processor: WebGLAdjustProcessor | null
  private color: HTMLCanvasElement | null = null
  private colorKey = ''
  private colorVersion = 0
  private comp: HTMLCanvasElement | null = null
  private layer: HTMLCanvasElement | null = null
  private readonly blurCache = new Map<string, HTMLCanvasElement>()

  constructor(processor: WebGLAdjustProcessor | null) {
    this.processor = processor
  }

  private ensure(canvas: HTMLCanvasElement | null, width: number, height: number): HTMLCanvasElement {
    if (canvas && canvas.width === width && canvas.height === height) return canvas
    return createCanvas(width, height)
  }

  /** The photo with Fine tune and the look applied, at the source's resolution */
  renderColor(source: RenderSource, width: number, height: number, adjustments: AdjustAdjustments): HTMLCanvasElement {
    const key = `${sourceId(source)}|${width}x${height}|${JSON.stringify(adjustments)}`
    if (this.color && key === this.colorKey) return this.color
    const canvas = this.ensure(this.color, width, height)
    const ctx = context2d(canvas)
    ctx.clearRect(0, 0, width, height)
    const gpu = isNeutral(adjustments) ? null : this.processor?.render(source, width, height, adjustments) ?? null
    if (isNeutral(adjustments) || gpu) {
      ctx.drawImage(gpu ?? source, 0, 0, width, height)
    } else {
      ctx.drawImage(source, 0, 0, width, height)
      const pixels = ctx.getImageData(0, 0, width, height)
      const out = ctx.createImageData(width, height)
      processPixels(pixels.data, width, height, adjustments, out.data)
      ctx.putImageData(out, 0, 0)
    }
    this.color = canvas
    this.colorKey = key
    this.colorVersion++
    this.blurCache.clear()
    return canvas
  }

  private blurredRegion(color: HTMLCanvasElement, blur: AdjustBlur, scale: number): { canvas: HTMLCanvasElement, x: number, y: number, key: string } | null {
    const [bx, by, bw, bh] = blurBounds(blur)
    const x = Math.max(0, Math.floor(bx * scale))
    const y = Math.max(0, Math.floor(by * scale))
    const right = Math.min(color.width, Math.ceil((bx + bw) * scale))
    const bottom = Math.min(color.height, Math.ceil((by + bh) * scale))
    const width = right - x
    const height = bottom - y
    if (width <= 0 || height <= 0) return null

    const key = `${this.colorVersion}|${scale}|${blur.id}|${blur.mode}|${blur.radius}|${blur.size}|${blur.points.join(',')}`
    const cached = this.blurCache.get(key)
    if (cached) return { canvas: cached, x, y, key }

    const radius = Math.max(0.5, blur.radius * scale)
    const pad = Math.ceil(radius * 2)
    const blurred = blurCanvas(paddedRegion(color, x, y, width, height, pad), radius)
    const masked = createCanvas(width, height)
    const ctx = context2d(masked)
    ctx.setTransform(scale, 0, 0, scale, -x, -y)
    traceBlurMask(ctx, blur)
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.globalCompositeOperation = 'source-in'
    ctx.drawImage(blurred, pad, pad, width, height, 0, 0, width, height)
    this.blurCache.set(key, masked)
    return { canvas: masked, x, y, key }
  }

  /** The edited photo (no crop, rotation or resize) at the source's resolution */
  compose(doc: AdjustDocument, source: RenderSource, width: number, height: number, imageSize: Size, assets: StickerAssets): HTMLCanvasElement {
    const scale = width / imageSize.width
    const color = this.renderColor(source, width, height, mergeAdjustments(doc.adjustments, doc.look))
    const comp = this.ensure(this.comp, width, height)
    this.comp = comp
    const ctx = context2d(comp)
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.globalCompositeOperation = 'source-over'
    ctx.clearRect(0, 0, width, height)
    ctx.drawImage(color, 0, 0)

    const liveKeys = new Set<string>()
    for (const blur of doc.blurs) {
      const region = this.blurredRegion(color, blur, scale)
      if (!region) continue
      liveKeys.add(region.key)
      ctx.drawImage(region.canvas, region.x, region.y)
    }
    // Drop blurs that no longer exist (undo, clear, a stroke that grew) so the cache stays the size of the document
    for (const key of Array.from(this.blurCache.keys())) {
      if (!liveKeys.has(key)) this.blurCache.delete(key)
    }

    if (doc.shapes.length) {
      const layer = this.ensure(this.layer, width, height)
      this.layer = layer
      const layerCtx = context2d(layer)
      layerCtx.setTransform(1, 0, 0, 1, 0, 0)
      layerCtx.clearRect(0, 0, width, height)
      layerCtx.setTransform(scale, 0, 0, scale, 0, 0)
      for (const shape of doc.shapes) drawAdjustShape(layerCtx, shape)
      ctx.drawImage(layer, 0, 0)
    }

    if (doc.stickers.length) {
      ctx.setTransform(scale, 0, 0, scale, 0, 0)
      for (const sticker of doc.stickers) drawAdjustSticker(ctx, sticker, assets)
      ctx.setTransform(1, 0, 0, 1, 0, 0)
    }
    return comp
  }

  /** Lets the browser reclaim the canvases (a save renders at full size) */
  dispose(): void {
    for (const canvas of [this.color, this.comp, this.layer, ...this.blurCache.values()]) {
      if (canvas) {
        canvas.width = 0
        canvas.height = 0
      }
    }
    this.blurCache.clear()
    this.color = null
    this.comp = null
    this.layer = null
  }
}

/** Halves until within 2× of the target, then a final high-quality step (one big downscale aliases) */
const resample = (input: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement => {
  let current = input
  while (current.width / 2 >= width && current.height / 2 >= height) {
    const half = createCanvas(current.width / 2, current.height / 2)
    const ctx = context2d(half)
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(current, 0, 0, half.width, half.height)
    current = half
  }
  if (current.width === width && current.height === height) return current
  const output = createCanvas(width, height)
  const ctx = context2d(output)
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(current, 0, 0, width, height)
  return output
}

export interface ExportAdjustedParams {
  doc: AdjustDocument
  image: HTMLImageElement
  processor: WebGLAdjustProcessor | null
  assets: StickerAssets
  mimeType: 'image/jpeg' | 'image/png'
}

/** Renders the document over the FULL-size photo, then crops, rotates, flips and resizes it. */
export const exportAdjustedImage = async ({ doc, image, processor, assets, mimeType }: ExportAdjustedParams): Promise<Blob> => {
  await assets.ensure(doc)
  const imageSize = { width: image.naturalWidth, height: image.naturalHeight }
  const renderer = new AdjustRenderer(processor)
  try {
    const comp = renderer.compose(doc, image, imageSize.width, imageSize.height, imageSize, assets)
    const frame = frameRectOf(doc.crop, imageSize)
    const cropped = createCanvas(frame.width, frame.height)
    const ctx = context2d(cropped)
    if (mimeType === 'image/jpeg') {
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, cropped.width, cropped.height)
    }
    const m = compose(translation(cropped.width / 2 - frame.cx, cropped.height / 2 - frame.cy), imageToFrame(doc.crop, imageSize))
    ctx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f)
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(comp, 0, 0, imageSize.width, imageSize.height)
    renderer.dispose()

    const target = outputSize(doc.crop, doc.resize, imageSize)
    const output = resample(cropped, target.width, target.height)
    return await new Promise<Blob>((resolve, reject) => {
      output.toBlob(blob => blob ? resolve(blob) : reject(new Error('adjust_export_failed')), mimeType, mimeType === 'image/jpeg' ? ADJUST_EXPORT_JPEG_QUALITY : undefined)
    })
  } finally {
    renderer.dispose()
  }
}
